import { useCallback, useState } from 'react';
import { inversionApi } from '../lib/api';
import {
  THEME_KINDS, buildThemeParams, computeTheme, defaultThemeParams, themeRequestBody,
} from '../lib/inversion-results';

/**
 * Layer-property theme maps for one inversion model entry (ThemeMapPanel).
 *
 * `run()` validates the form values, computes the map (in the browser for client-side
 * models; through POST /api/inversion/{id}/theme when the entry has a stored `backendId`)
 * and hands the GeoJSON to `onAddLayer(geojson, name, 'grid')`, which adds it as a
 * result layer drawn with the cool-to-warm ramp and the map's colour-scale legend.
 *
 *   entry       - { id, name, model, cells, backendId? }
 *   onAddLayer  - (geojson, name, kind) => void
 */
export default function useThemeMap({ entry, onAddLayer }) {
  const [kind, setKindState] = useState('top_depth');
  const [form, setForm] = useState(() => defaultThemeParams('top_depth', entry?.model));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [last, setLast] = useState(null); // { name, min, max, n }

  const setKind = useCallback((k) => {
    setKindState(k);
    setForm(defaultThemeParams(k, entry?.model));
    setError(null);
  }, [entry]);

  const setField = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);

  const run = useCallback(async () => {
    setError(null);
    if (!entry) return;
    const built = buildThemeParams(kind, form);
    if (built.error) { setError(built.error); return; }
    const georeferenced = entry.cells?.[0]?.lon !== undefined;
    if (!georeferenced) { setError('Define the coordinates of this model first'); return; }
    setBusy(true);
    try {
      let geojson;
      if (entry.backendId) {
        const res = await inversionApi.theme(entry.backendId, themeRequestBody(kind, built.params));
        geojson = res.data;
      } else {
        geojson = computeTheme(entry.cells, kind, built.params);
      }
      if (!geojson.features.length) { setError('No column of this model satisfies the criteria'); return; }
      const label = THEME_KINDS.find((t) => t.id === kind)?.label.split(' (')[0] || kind;
      const name = `${label} — ${entry.name}`;
      geojson.metadata = { ...geojson.metadata, title: name };
      onAddLayer(geojson, name, 'grid', { source: { kind: 'model', id: entry.id, name: entry.name } });
      setLast({ name, min: geojson.metadata.min, max: geojson.metadata.max, n: geojson.features.length });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  }, [entry, kind, form, onAddLayer]);

  return { kind, setKind, form, setField, run, busy, error, last };
}
